import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, MailCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AuthLayout, FormError } from '../components/AuthLayout';
import { API_URL } from '../utils/api';
import { getErrorMessage } from '../utils/errors';
import { useDocumentTitle } from '../hooks/use-document-title';

export default function ForgotPasswordPage() {
  useDocumentTitle('Forgot password');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setSending(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(getErrorMessage(err, 'We could not send the email. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        footer={<Link to="/login" className="text-gold hover:underline font-medium">Back to sign in</Link>}
      >
        <div className="text-center space-y-3" role="status">
          <MailCheck className="h-10 w-10 mx-auto text-gold" aria-hidden="true" />
          <p className="text-ink">
            If an account exists for <strong>{email.trim()}</strong>, we've sent a link to reset your password.
          </p>
          <p className="text-sm text-ink/60">The link works for one hour. Check your spam folder if you don't see it.</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot your password?"
      subtitle="Enter your email and we'll send you a link to choose a new one."
      footer={<>Remembered it? <Link to="/login" className="text-gold hover:underline font-medium">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-5" noValidate>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            className="mt-2"
            aria-invalid={Boolean(error) || undefined}
          />
        </div>
        <FormError message={error} />
        <Button type="submit" disabled={sending} className="w-full bg-ink text-paper hover:bg-ink/90">
          {sending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {sending ? 'Sending…' : 'Send reset link'}
        </Button>
        <p className="text-xs text-ink/60 text-center">
          Signed up by phone? Use the Phone &amp; PIN tab on the sign-in page, or set a new PIN from the phone menu.
        </p>
      </form>
    </AuthLayout>
  );
}
